/**
 * Streamed generation of an SOP's scenario types. A new set replaces the whole list, one
 * type per outcome of the protocol's graph, so the server drops the old types' scenarios
 * and the bucketing of past tickets starts again. Streamed rows are a preview until the
 * persisted set replaces them. A refused generation leaves its reason with the SOP's note: a toast
 * would close with the confirm dialog before it is read.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { aiFunctionService } from '@/services/ai-function.service';
import { counted } from '../format';
import { toLocalType, type ScenarioType } from '../types';
import { refusalOf } from './refusal';
import type { HeldRefusal } from './use-held-refusal';

/** What the caller does once a type set lands; the hook owns only the streaming state. */
interface TypeSetHandlers {
	/** Landed: drop whatever the set it replaced was holding. */
	onReplaced: (types: ScenarioType[]) => void;
	/** The replacement is on the server, so the past tickets belong to it now. */
	onMapPastTickets: () => void;
}

export const useTypeGeneration = (sopId: string, { hold, dismiss }: Pick<HeldRefusal, 'hold' | 'dismiss'>) => {
	const [generatingTypes, setGeneratingTypes] = useState(false);
	// Elements shown as they arrive, replaced wholesale by the persisted set.
	// null = no stream in flight, which is not the same as one that found nothing.
	const [streamingTypes, setStreamingTypes] = useState<ScenarioType[] | null>(null);
	const typeStreamEndRef = useRef<HTMLDivElement>(null);

	const generateTypes = useCallback(
		async ({ onReplaced, onMapPastTickets }: TypeSetHandlers) => {
			setGeneratingTypes(true);
			setStreamingTypes([]);
			dismiss();
			try {
				const batch = await aiFunctionService.streamScenarioTypes(
					{ sopId },
					{ onElement: (type) => setStreamingTypes((prev) => [...(prev ?? []), toLocalType(sopId, type)]) }
				);
				if (!batch.scenarioTypes.length) {
					toast.error('No scenario types came back');
					return;
				}
				onReplaced(batch.scenarioTypes.map((t) => toLocalType(sopId, t)));
				if (!batch.persisted) {
					toast.warning('Generated, but saving to the database failed, so this set lives only in this browser');
				}
				toast.success(`${counted(batch.scenarioTypes.length, 'scenario type')} mapped`);
				if (batch.persisted) onMapPastTickets();
			} catch (err) {
				hold({ sopId, typeId: null }, refusalOf(err, 'Could not generate scenario types'));
			} finally {
				setGeneratingTypes(false);
				setStreamingTypes(null);
			}
		},
		[sopId, hold, dismiss]
	);

	return { generatingTypes, streamingTypes, typeStreamEndRef, generateTypes };
};

/**
 * Keeps the newest streamed row in view, and stops the moment a type is selected
 * so the stream never scrolls out from under a click.
 */
export const useStreamFollow = (
	count: number,
	endRef: React.RefObject<HTMLDivElement | null>,
	paused: boolean
): void => {
	useEffect(() => {
		if (count > 0 && !paused) endRef.current?.scrollIntoView({ block: 'nearest' });
	}, [count, paused, endRef]);
};
